import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe, TrendingUp, TrendingDown, AlertTriangle, Activity,
  Loader2, RefreshCw, Zap, BarChart3, Target,
  ChevronDown, ChevronUp, Shield, Building2,
  Lightbulb, ShieldAlert, Minus, FileText, Calendar, Clock, History
} from 'lucide-react';

const COUNTRY_NAMES = {
  EU: 'EU Universal', PL: 'Poland', FI: 'Finland', SE: 'Sweden',
  NO: 'Norway', DK: 'Denmark', EE: 'Estonia', LV: 'Latvia',
  LT: 'Lithuania', DE: 'Germany', AT: 'Austria', BE: 'Belgium',
  BG: 'Bulgaria', HR: 'Croatia', CY: 'Cyprus', CZ: 'Czechia',
  FR: 'France', GR: 'Greece', HU: 'Hungary', IE: 'Ireland',
  IT: 'Italy', LU: 'Luxembourg', MT: 'Malta', NL: 'Netherlands',
  PT: 'Portugal', RO: 'Romania', SK: 'Slovakia', SI: 'Slovenia',
  ES: 'Spain', GB: 'United Kingdom', CH: 'Switzerland',
};

const CATEGORY_LABELS = {
  grants: 'Grants',
  procurement: 'Procurement',
  permits: 'Permits',
  eia: 'Environmental',
  trade_press: 'Trade Press',
  enrichment: 'Enrichment',
};

const CATEGORY_COLORS = {
  grants: 'bg-green-100 text-green-700 border-green-200',
  procurement: 'bg-blue-100 text-blue-700 border-blue-200',
  permits: 'bg-orange-100 text-orange-700 border-orange-200',
  eia: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  trade_press: 'bg-purple-100 text-purple-700 border-purple-200',
  enrichment: 'bg-gray-100 text-gray-700 border-gray-200',
};

const IntelligenceOverview = ({
  profileId,
  profileName,
  targetConfig: targetConfigProp,
  loadIntelligenceOverview,
  loadSourceRegistry,
  loadLatestReport,
  loadReportHistory,
  loadProfileTargetConfig,
  onDrillDown,
}) => {
  const [overview, setOverview] = useState(null);
  const [registry, setRegistry] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPattern, setExpandedPattern] = useState(null);
  const [localTargetConfig, setLocalTargetConfig] = useState(targetConfigProp || null);
  const [showPipelineDetail, setShowPipelineDetail] = useState(false);
  const [showSourceGrid, setShowSourceGrid] = useState(false);
  const [showPatterns, setShowPatterns] = useState(false);
  const [showReportBody, setShowReportBody] = useState(false);
  const [expandedOpp, setExpandedOpp] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [selectedReportIdx, setSelectedReportIdx] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  // Next report date: next Sunday at 06:00 EET (UTC+3 in summer / UTC+2 in winter).
  // EET offset is always +2; EEST is +3 but cron runs at 04:00 UTC = 06:00 EET year-round.
  const getNextReportDate = () => {
    const now = new Date();
    const day = now.getUTCDay(); // 0=Sun
    const daysUntilSunday = day === 0 ? 7 : 7 - day;
    const next = new Date(now);
    next.setUTCDate(next.getUTCDate() + daysUntilSunday);
    next.setUTCHours(4, 0, 0, 0); // 04:00 UTC = 06:00 EET
    if (next <= now) next.setUTCDate(next.getUTCDate() + 7);
    return next;
  };

  const nextReportDate = getNextReportDate();
  const daysUntilReport = Math.ceil((nextReportDate - new Date()) / (1000 * 60 * 60 * 24));

  const targetConfig = targetConfigProp || localTargetConfig;
  const countryPack = targetConfig?.country_pack || {};
  const intelligenceCountries = countryPack.intelligence_countries?.length > 0
    ? countryPack.intelligence_countries
    : targetConfig?.target_geographies || [];

  const loadData = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    setError(null);
    try {
      const promises = [
        loadIntelligenceOverview(profileId),
        loadSourceRegistry(),
        loadReportHistory ? loadReportHistory(profileId) : loadLatestReport(profileId),
      ];
      // Load target_config if not provided via props
      if (!targetConfigProp && loadProfileTargetConfig) {
        promises.push(loadProfileTargetConfig(profileId));
      }
      const results = await Promise.all(promises);
      setOverview(results[0]);
      setRegistry(results[1]);
      if (loadReportHistory) {
        const history = results[2] || [];
        setReportHistory(history);
        setReport(history[0] || null);
        setSelectedReportIdx(0);
      } else {
        setReport(results[2]);
      }
      if (results[3] !== undefined) {
        setLocalTargetConfig(results[3]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileId, targetConfigProp, loadIntelligenceOverview, loadSourceRegistry, loadLatestReport, loadReportHistory, loadProfileTargetConfig]);

  useEffect(() => { loadData(); }, [loadData]);

  // Group registry sources by country
  const sourcesByCountry = {};
  const EU_MEMBERS = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'];
  
  registry.forEach(source => {
    if (source.is_eu_universal) {
      intelligenceCountries.forEach(cc => {
        if (EU_MEMBERS.includes(cc)) {
          if (!sourcesByCountry[cc]) sourcesByCountry[cc] = [];
          sourcesByCountry[cc].push({ ...source, target_country: cc });
        }
      });
    } else if (intelligenceCountries.includes(source.country_code)) {
      if (!sourcesByCountry[source.country_code]) sourcesByCountry[source.country_code] = [];
      sourcesByCountry[source.country_code].push(source);
    }
  });

  const sourceCounts = overview?.source_counts || {};
  const filteredCounts = overview?.filtered_counts || {};
  const scoredSourceCounts = overview?.scored_source_counts || {};
  const highConviction = overview?.high_conviction_signals || [];

  // Country-pack figures are the ones the rest of this screen describes.
  // Show scored signals as the headline (what users care about).
  // Raw "sent to AI" count shown as subtitle for transparency.
  const countryPackSignals30d = overview?.total_scored_countrypack_30d ?? 0;
  const countryPackSentToAI30d = overview?.total_signals_countrypack_30d ?? 0;
  const countryPackFiltered30d = overview?.total_filtered_countrypack_30d ?? 0;
  const allCollectorsSignals30d = overview?.total_signals_30d ?? 0;

  const sourceCountTotal = Object.values(sourceCounts).reduce((a, b) => a + b, 0);
  const filteredTotal = Object.values(filteredCounts).reduce((a, b) => a + b, 0);
  const scoredSourceTotal = Object.values(scoredSourceCounts).reduce((a, b) => a + b, 0);

  const drill = (patch) => {
    if (onDrillDown) onDrillDown({ ...patch, origin: 'country_pack', goToSignals: true });
  };

  // ─── Parse the weekly report ───
  // Postgres jsonb arrives as an object, but a string is tolerated so the panel
  // cannot be taken down by an unexpected serialisation.
  const asObject = (value, fallback) => {
    if (!value) return fallback;
    if (typeof value === 'string') {
      try { return JSON.parse(value) || fallback; } catch { return fallback; }
    }
    return value;
  };

  let detectedPatterns = [];
  let marketInsight = '';
  let recommendedActions = [];
  let sourceHealth = {};
  let growthSegments = [];
  let opportunities = [];
  let risks = [];
  let momentum = null;
  let reportStatus = null;

  if (report) {
    const patterns = asObject(report.detected_patterns, []);
    detectedPatterns = Array.isArray(patterns) ? patterns : [];

    const summary = asObject(report.source_summary, {});
    marketInsight = summary.market_insight || '';
    recommendedActions = Array.isArray(summary.recommended_actions) ? summary.recommended_actions : [];
    sourceHealth = summary.source_health || {};

    // report_sections is the richer body written by the current Pattern
    // Detector. Reports generated before it existed simply have none, and fall
    // back to the legacy fields above.
    const sections = asObject(report.report_sections, {});
    growthSegments = Array.isArray(sections.growth_segments) ? sections.growth_segments : [];
    opportunities = Array.isArray(sections.opportunities) ? sections.opportunities : [];
    risks = Array.isArray(sections.risks) ? sections.risks : [];
    momentum = sections.momentum || null;
    reportStatus = sections.status || null;

    if (report.ai_insight && !marketInsight) {
      marketInsight = report.ai_insight;
    }
  }

  // Source tiles: split into the ones actually producing and the ones that are
  // silent, so a wall of zeroes does not bury the handful that matter.
  const allSourceTiles = Object.entries(sourcesByCountry)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([country, sources]) => sources.map(source => ({
      country,
      source,
      scored: scoredSourceCounts[source.source_name] || 0,
      passed: sourceCounts[source.source_name] || 0,
      filtered: filteredCounts[source.source_name] || 0,
    })));

  const producingTiles = allSourceTiles.filter(t => t.scored > 0 || t.passed > 0);
  const quietTiles = allSourceTiles.filter(t => t.scored === 0 && t.passed === 0);
  const enabledSourceCount = allSourceTiles.filter(t => t.source.is_enabled).length;
  const visibleTiles = showSourceGrid ? allSourceTiles : producingTiles;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading Intelligence Overview...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-600 text-sm">{error}</p>
        <button onClick={loadData} className="mt-3 text-sm text-primary-600 hover:underline">Retry</button>
      </div>
    );
  }

  if (!countryPack.enabled) {
    return (
      <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
        <Globe className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <h3 className="text-base font-semibold text-gray-900 mb-2">Country Intelligence Pack Not Enabled</h3>
        <p className="text-gray-500 text-sm max-w-md mx-auto mb-4">
          Enable the Country Intelligence Pack in your profile settings to start monitoring
          grants, procurement, permits, and trade press across your target countries.
        </p>
        <p className="text-xs text-gray-400 max-w-sm mx-auto">
          Go to <strong>Edit Profile</strong> and scroll down to the <strong>Country Intelligence Pack</strong> section
          to enable it. You can select which countries to monitor, add industry codes (PKD/NACE),
          and procurement codes (CPV) for more precise filtering.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-4.5 h-4.5 text-primary-600" />
            Intelligence Overview
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {profileName} · {intelligenceCountries.map(c => COUNTRY_NAMES[c] || c).join(', ')}
            {' '}({intelligenceCountries.length})
          </p>
        </div>
        <button
          onClick={loadData}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats Row — four distinct numbers, no repeats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={() => drill({ country: '', eventType: '', minScore: 0 })}
          className="bg-white border border-gray-200 rounded-xl p-3 text-left hover:border-primary-300 hover:shadow-sm transition-colors"
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <Target className="w-3.5 h-3.5 text-green-500" />
            <span className="text-[11px] font-medium text-gray-500">Approved (30d)</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{countryPackSignals30d}</p>
          <p className="text-[10px] text-primary-600 mt-0.5">View signals →</p>
        </button>
        <button
          type="button"
          onClick={() => drill({ country: '', eventType: '', minScore: 70 })}
          className="bg-white border border-gray-200 rounded-xl p-3 text-left hover:border-primary-300 hover:shadow-sm transition-colors"
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <Shield className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-[11px] font-medium text-gray-500">High Conviction</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{highConviction.length}</p>
          <p className="text-[10px] text-primary-600 mt-0.5">Score 70+, 2+ src →</p>
        </button>
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[11px] font-medium text-gray-500">Opportunities</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{opportunities.length}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {opportunities.length > 0
              ? `${opportunities.filter(o => o.urgency === 'high').length} urgent`
              : 'From weekly report'}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Activity className="w-3.5 h-3.5 text-orange-500" />
            <span className="text-[11px] font-medium text-gray-500">Sources Live</span>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {producingTiles.length}
            <span className="text-xs font-medium text-gray-400"> / {enabledSourceCount}</span>
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {quietTiles.length > 0 ? `${quietTiles.length} quiet` : 'All producing'}
          </p>
        </div>
      </div>

      {/* Pipeline detail — collapsed by default; only useful when diagnosing */}
      <div className="-mt-3">
        <button
          type="button"
          onClick={() => setShowPipelineDetail(v => !v)}
          className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
        >
          {showPipelineDetail ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {showPipelineDetail ? 'Hide pipeline detail' : 'Show pipeline detail'}
        </button>
        {showPipelineDetail && (
          <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="block text-gray-500">Collected (country pack)</span>
              <span className="font-semibold text-gray-900">{countryPackSentToAI30d + countryPackFiltered30d}</span>
            </div>
            <div>
              <span className="block text-gray-500">Removed by pre-filter</span>
              <span className="font-semibold text-gray-900">{countryPackFiltered30d}</span>
            </div>
            <div>
              <span className="block text-gray-500">Sent to AI</span>
              <span className="font-semibold text-gray-900">{countryPackSentToAI30d}</span>
            </div>
            <div>
              <span className="block text-gray-500">All collectors (30d)</span>
              <span className="font-semibold text-gray-900">{allCollectorsSignals30d}</span>
            </div>
          </div>
        )}
      </div>

      {/* Next Report Indicator */}
      <div className="flex items-center gap-2 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">
        <Calendar className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
        <span className="text-xs text-primary-800">
          Next report: <strong>{nextReportDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</strong>
          {daysUntilReport === 1 ? ' (tomorrow)' : ` (in ${daysUntilReport} days)`}
        </span>
        <Clock className="w-3 h-3 text-primary-400 ml-auto flex-shrink-0" />
        <span className="text-[11px] text-primary-500">06:00 EET</span>
      </div>

      {/* Weekly Report — compact header with expandable body */}
      {report ? (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div
            className="flex items-start justify-between gap-3 cursor-pointer"
            onClick={() => setShowReportBody(v => !v)}
          >
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary-600" />
                Weekly Report
                {report.report_period && (
                  <span className="text-xs font-normal text-gray-400">{report.report_period}</span>
                )}
                {reportStatus === 'quiet_period' && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                    Quiet period
                  </span>
                )}
              </h3>
              {/* Always-visible one-liner insight */}
              {marketInsight && (
                <p className={`text-xs text-gray-600 mt-1.5 leading-relaxed ${!showReportBody ? 'line-clamp-2' : ''}`}>
                  {marketInsight}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {momentum?.direction && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                  momentum.direction === 'accelerating' ? 'bg-green-50 text-green-700 border-green-200' :
                  momentum.direction === 'slowing' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                  'bg-gray-50 text-gray-600 border-gray-200'
                }`}>
                  {momentum.direction === 'accelerating' ? <TrendingUp className="w-3 h-3" /> :
                   momentum.direction === 'slowing' ? <TrendingDown className="w-3 h-3" /> :
                   <Minus className="w-3 h-3" />}
                  {momentum.direction}
                </span>
              )}
              {showReportBody ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
          </div>

          {showReportBody && (
            <div className="mt-4 space-y-4">
              {reportStatus === 'quiet_period' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-900">
                    Nothing was approved this period. The report below explains what the sources did.
                  </p>
                </div>
              )}

              {/* Growth segments — compact table */}
              {growthSegments.length > 0 && (
                <div>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                    Market Segments
                  </span>
                  <div className="space-y-1.5">
                    {growthSegments.map((seg, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          seg.direction === 'growing' ? 'bg-green-500' :
                          seg.direction === 'contracting' ? 'bg-red-500' : 'bg-gray-400'
                        }`} />
                        <span className="font-medium text-gray-900 flex-1 truncate">{seg.segment}</span>
                        <span className="text-gray-400 flex-shrink-0">{seg.signal_count || 0} sig</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended actions — compact */}
              {recommendedActions.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <span className="text-[11px] font-semibold text-green-800 block mb-1">Actions</span>
                  <ul className="space-y-1">
                    {recommendedActions.slice(0, 3).map((action, i) => (
                      <li key={i} className="text-xs text-green-900 flex items-start gap-1.5">
                        <span className="text-green-500 mt-0.5 flex-shrink-0">·</span>
                        <span className="line-clamp-2">{action}</span>
                      </li>
                    ))}
                    {recommendedActions.length > 3 && (
                      <li className="text-[11px] text-green-700">+{recommendedActions.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Risks — compact */}
              {risks.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <span className="text-[11px] font-semibold text-orange-800 flex items-center gap-1 mb-1">
                    <ShieldAlert className="w-3 h-3" /> Risks
                  </span>
                  <ul className="space-y-1">
                    {risks.map((r, i) => (
                      <li key={i} className="text-xs text-orange-900">
                        {r.risk}
                        {r.mitigation && (
                          <span className="block text-[11px] text-orange-700 mt-0.5">→ {r.mitigation}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Detected patterns — collapsed */}
              {detectedPatterns.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowPatterns(v => !v); }}
                    className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                  >
                    {showPatterns ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {detectedPatterns.length} detected {detectedPatterns.length === 1 ? 'pattern' : 'patterns'}
                  </button>
                  {showPatterns && (
                    <div className="space-y-1.5 mt-2">
                      {detectedPatterns.map((pattern, idx) => (
                        <div
                          key={idx}
                          className="border border-gray-100 rounded-lg p-2.5 cursor-pointer hover:border-primary-200"
                          onClick={(e) => { e.stopPropagation(); setExpandedPattern(expandedPattern === idx ? null : idx); }}
                        >
                          <div className="flex items-center gap-2">
                            <Zap className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                            <span className="text-xs font-medium text-gray-900 flex-1">{pattern.pattern}</span>
                            {pattern.confidence && (
                              <span className="text-[11px] text-gray-400">{(pattern.confidence * 100).toFixed(0)}%</span>
                            )}
                          </div>
                          {expandedPattern === idx && (
                            <div className="mt-1.5 ml-5">
                              {pattern.action && <p className="text-[11px] text-gray-600 mb-1">{pattern.action}</p>}
                              {(pattern.companies || []).map((company, cidx) => (
                                <span key={cidx} className="inline-block bg-primary-50 text-primary-700 text-[11px] px-1.5 py-0.5 rounded mr-1 mb-0.5">
                                  {company}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="text-[11px] text-gray-400 pt-2 border-t border-gray-100">
                {report.generated_at ? new Date(report.generated_at).toLocaleString() : 'N/A'}
                {report.signals_analysed > 0 && ` · ${report.signals_analysed} signals analysed`}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <FileText className="w-6 h-6 text-gray-300 mx-auto mb-1.5" />
          <h3 className="text-xs font-semibold text-gray-900 mb-0.5">No weekly report yet</h3>
          <p className="text-[11px] text-gray-500">
            First report arrives on{' '}
            <strong>{nextReportDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</strong> at 06:00 EET.
          </p>
        </div>
      )}

      {/* Report History — collapsible list of previous reports */}
      {reportHistory.length > 1 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <button
            type="button"
            onClick={() => setShowHistory(v => !v)}
            className="flex items-center gap-2 w-full text-left"
          >
            <History className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">Previous Reports</span>
            <span className="text-xs text-gray-400">{reportHistory.length - 1} archived</span>
            <span className="ml-auto">
              {showHistory ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </span>
          </button>
          {showHistory && (
            <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
              {reportHistory.map((r, idx) => {
                const rSections = typeof r.report_sections === 'string'
                  ? (() => { try { return JSON.parse(r.report_sections); } catch { return {}; } })()
                  : (r.report_sections || {});
                const rStatus = rSections.status || null;
                const isSelected = idx === selectedReportIdx;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setSelectedReportIdx(idx);
                      setReport(r);
                      setShowReportBody(false);
                      setShowHistory(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'bg-primary-50 border border-primary-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-medium ${isSelected ? 'text-primary-700' : 'text-gray-900'}`}>
                        {r.report_period}
                      </span>
                      {rStatus === 'quiet_period' && (
                        <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-600">quiet</span>
                      )}
                      {rStatus === 'ok' && r.signals_analysed > 0 && (
                        <span className="ml-1.5 text-[10px] text-gray-400">{r.signals_analysed} signals</span>
                      )}
                    </div>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {r.generated_at ? new Date(r.generated_at).toLocaleDateString() : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Opportunities — primary actionable section (replaces redundant High Conviction list) */}
      {opportunities.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            Opportunities
            <span className="text-xs font-normal text-gray-400">
              {opportunities.length} targets · {opportunities.filter(o => o.urgency === 'high').length} urgent
            </span>
          </h3>
          <div className="space-y-1.5">
            {opportunities.map((o, i) => (
              <div
                key={i}
                className="border border-gray-100 rounded-lg p-2.5 cursor-pointer hover:border-primary-200 transition-colors"
                onClick={() => setExpandedOpp(expandedOpp === i ? null : i)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">{o.company}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${
                      o.urgency === 'high' ? 'bg-red-100 text-red-700' :
                      o.urgency === 'low' ? 'bg-gray-100 text-gray-600' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {o.urgency}
                    </span>
                    {o.score > 0 && (
                      <span className="text-[11px] font-bold text-gray-700 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                        {o.score}
                      </span>
                    )}
                    {expandedOpp === i ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                  </div>
                </div>
                {expandedOpp === i && (
                  <div className="mt-2 ml-5.5 space-y-1.5">
                    {o.why && <p className="text-xs text-gray-700">{o.why}</p>}
                    {o.action && (
                      <p className="text-[11px] text-primary-700 bg-primary-50 border border-primary-100 rounded px-2 py-1 inline-block">
                        → {o.action}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Source Activity — mechanics, so it sits below the actionable sections */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary-600" />
            Source Activity
            <span className="text-xs font-normal text-gray-400">last 30 days</span>
          </h3>
          {allSourceTiles.length > 0 && (
            <button
              type="button"
              onClick={() => setShowSourceGrid(v => !v)}
              className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 flex-shrink-0"
            >
              {showSourceGrid ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showSourceGrid ? 'Show producing only' : `Show all ${allSourceTiles.length}`}
            </button>
          )}
        </div>

        {/* One-line health summary, so the panel is useful while collapsed */}
        <p className="text-xs text-gray-500 mb-4">
          {allSourceTiles.length === 0
            ? 'No sources configured for your intelligence countries.'
            : <>
                <span className="font-medium text-gray-700">{producingTiles.length} live</span>
                {quietTiles.length > 0 && <> · {quietTiles.length} quiet</>}
                {scoredSourceTotal > 0 && <> · {scoredSourceTotal} scored signals</>}
                {showPipelineDetail && sourceCountTotal > 0 && <> · {sourceCountTotal} sent to AI</>}
                {showPipelineDetail && filteredTotal > 0 && <> · {filteredTotal} pre-filtered</>}
              </>}
        </p>

        {visibleTiles.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {visibleTiles.map(({ country, source, scored, passed, filtered }) => {
              const health = sourceHealth[source.source_name];
              const healthStatus = health?.status || (passed > 0 ? 'healthy' : 'empty');

              return (
                <button
                  type="button"
                  onClick={() => drill({ country, eventType: '', minScore: 0 })}
                  key={`${country}-${source.source_name}`}
                  className={`text-left w-full border rounded-lg p-2.5 transition-colors hover:border-primary-300 hover:shadow-sm ${
                    !source.is_enabled ? 'border-gray-200 bg-gray-50 opacity-60' :
                    scored > 0 ? 'border-green-200 bg-green-50/30' :
                    healthStatus === 'degraded' ? 'border-yellow-200 bg-yellow-50/30' :
                    'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-[11px] font-bold text-gray-900 uppercase tracking-wide truncate">
                      {source.source_name}
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      !source.is_enabled ? 'bg-gray-300' :
                      scored > 0 ? 'bg-green-500' :
                      passed > 0 ? 'bg-yellow-500' :
                      'bg-gray-300'
                    }`} />
                  </div>
                  <div className="flex items-baseline justify-between gap-1">
                    <span className="text-lg font-bold text-gray-800 leading-none">{scored}</span>
                    <span className="text-[11px] text-gray-500 truncate">
                      {COUNTRY_NAMES[country] || country}
                    </span>
                  </div>
                  <span className={`text-[10px] mt-1 inline-block px-1 py-0.5 rounded border ${CATEGORY_COLORS[source.category] || 'bg-gray-100 text-gray-600'}`}>
                    {CATEGORY_LABELS[source.category] || source.category}
                  </span>
                  {showPipelineDetail && (passed > 0 || filtered > 0) && (
                    <span className="text-[10px] text-gray-400 mt-1 block">
                      {passed} to AI{filtered > 0 && ` · ${filtered} filtered`}
                    </span>
                  )}
                  {!source.is_enabled && (
                    <span className="text-[10px] text-gray-400 mt-1 block">Not enabled</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {visibleTiles.length === 0 && allSourceTiles.length > 0 && (
          <p className="text-sm text-gray-500">
            No source produced a signal in the last 30 days.
            {' '}
            <button type="button" onClick={() => setShowSourceGrid(true)} className="text-primary-600 hover:underline">
              Show all {allSourceTiles.length} configured sources
            </button>
          </p>
        )}
      </div>

      {/* No data state */}
      {!report && highConviction.length === 0 && countryPackSignals30d === 0 && (
        <div className="text-center py-8 bg-white border border-gray-200 rounded-xl">
          <Globe className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">No intelligence data yet</h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            The Country Pack Collector runs daily at 05:00 EET. Intelligence data will appear here
            once signals are collected from your configured sources.
          </p>
        </div>
      )}
    </div>
  );
};

export default IntelligenceOverview;
